import React, { useCallback, useState, useEffect } from 'react';
import { api } from '../services/firebaseFunctions';
import { useIsMobile } from '../utils/isMobile';
import { recordRoleSelections } from '../utils/roleFrequency';
import UserManagerDesktop from './UserManagerDesktop';
import UserManagerMobile from './UserManagerMobile';

const GROUP_BY_OPTIONS = [
  { value: 'company',         label: 'Company' },
  { value: 'creation_month',  label: 'Creation Month' },
  { value: 'last_login',      label: 'Last Login' },
];

const STATUS_OPTIONS = [
  { value: 'all',      label: 'All' },
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
];

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

function groupUsers(users, groupBy) {
  const groups = {};

  if (groupBy === 'company') {
    users.forEach(user => {
      const key = user.company_name || 'No Company';
      if (!groups[key]) groups[key] = [];
      groups[key].push(user);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, users]) => ({ label, users }));
  }

  if (groupBy === 'creation_month') {
    users.forEach(user => {
      const raw = user.createdAt || user.metadata?.creationTime;
      const date = raw ? new Date(raw) : null;
      const sortKey = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        : '0000-00';
      const label = date
        ? date.toLocaleString('default', { month: 'long', year: 'numeric' })
        : 'Unknown';
      if (!groups[sortKey]) groups[sortKey] = { label, users: [] };
      groups[sortKey].users.push(user);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([, { label, users }]) => ({ label, users }));
  }

  if (groupBy === 'last_login') {
    const now = Date.now();
    users.forEach(user => {
      const weeksAgo = user.lastSignIn
        ? Math.floor((now - new Date(user.lastSignIn).getTime()) / MS_PER_WEEK)
        : null;
      const sortKey = weeksAgo === null ? 'zzz' : String(weeksAgo).padStart(6, '0');
      const label = weeksAgo === null
        ? 'Never signed in'
        : weeksAgo === 0 ? 'This week'
        : weeksAgo === 1 ? '1 week ago'
        : `${weeksAgo} weeks ago`;
      if (!groups[sortKey]) groups[sortKey] = { label, users: [] };
      groups[sortKey].users.push(user);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, { label, users }]) => ({ label, users }));
  }

  return [{ label: null, users }];
}

const byEmail = (a, b) => (a.email || '').localeCompare(b.email || '');

// Admin actions must target the Firestore doc id (`id`), not the Auth uid (`uid`).
// They are equal once a user has signed in, but a pre-created doc lives under a temp id
// until then, and targeting `uid` there made updateUser create a duplicate doc.
// Guests (Auth-only users) have no doc, so `uid` is the intended fallback for them.
const docIdOf = (user) => user.id || user.uid;

// ── Form drafts ────────────────────────────────────────────────────────────
// The desktop /users form is a list of drafts, one per section. A 'create' draft
// saves through createUser, an 'edit' draft (pushed by the Edit action on a row)
// through updateUser. Saving walks them one at a time — this is UI scaffolding for
// entering several users in one sitting, not a bulk endpoint.

let draftSeq = 0;
const nextDraftKey = () => `draft-${++draftSeq}`;

const emptyDraft = () => ({
  key: nextDraftKey(),
  mode: 'create',
  docId: null,
  email: '',
  displayName: '',
  company: '',
  roles: [],
  errors: {},
  serverError: null,
});

// `takenEmails` holds every address already spoken for — existing users, guests,
// and the create drafts earlier in this same batch. Checking here means a clone
// left unedited is caught in the form instead of by a failed createUser call.
function validateDraft(draft, takenEmails) {
  const errors = {};
  if (draft.mode === 'create') {
    const email = (draft.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      errors.email = 'Valid email address is required';
    } else if (takenEmails.has(email)) {
      errors.email = 'That address already belongs to another user — change it before saving';
    }
    // Only enforced on create: an existing global admin legitimately has no company.
    if (!draft.company) errors.company = 'Company is required';
  }
  if (!draft.roles.length) errors.roles = 'At least one role is required';
  return errors;
}

function UserManager() {
  const isMobile = useIsMobile();

  const [users, setUsers] = useState([]);
  const [groupBy, setGroupBy] = useState('company');
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [unassignedUsers, setUnassignedUsers] = useState([]);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [createData, setCreateData] = useState({ email: '', displayName: '', roles: [], company: '' });
  const [formErrors, setFormErrors] = useState({});
  const [editUserData, setEditUserData] = useState({
    displayName: '', email: '', company: '', roles: [], status: 'active'
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userEmail: '' });

  // Desktop form drafts — the form stays hidden until Add User puts one here.
  const [drafts, setDrafts] = useState([]);
  const [savingDrafts, setSavingDrafts] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [companiesData, rolesData, usersData] = await Promise.all([
        api.listCompanies(),
        api.listRoles(),
        api.listUsers()
      ]);
      setCompanies(companiesData);
      setRoles(rolesData);
      setUsers(usersData);
      const unassignedData = await api.listUnassignedUsers();
      setUnassignedUsers(unassignedData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateData({ email: '', displayName: '', roles: [], company: '' });
    setFormErrors({});
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setCreateData({ email: '', displayName: '', roles: [], company: '' });
    setFormErrors({});
  };

  const handleCreateUser = async () => {
    const errors = {};
    if (!createData.email || !createData.email.includes('@')) errors.email = 'Valid email address is required';
    if (!createData.company) errors.company = 'Company is required';
    if (!createData.roles.length) errors.roles = 'At least one role is required';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    try {
      setError(null);
      setFormErrors({});
      await api.createUser({
        email: createData.email.trim().toLowerCase(),
        displayName: createData.displayName.trim() || null,
        company: createData.company,
        roles: createData.roles
      });
      setSuccess('User created successfully');
      setTimeout(() => setSuccess(null), 3000);
      handleCloseCreateDialog();
      loadData();
    } catch (err) {
      setError(err.message);
      console.error('Error creating user:', err);
    }
  };

  // These three are wrapped so their identity is stable across renders. The form
  // memoises its roles row on them; recreating them each render would defeat that
  // and put the per-keystroke re-render of every role pill straight back.
  const handleAddDraft = useCallback(() => {
    setDrafts(prev => [...prev, emptyDraft()]);
  }, []);

  const handleRemoveDraft = useCallback((key) => {
    setDrafts(prev => prev.filter(d => d.key !== key));
  }, []);

  const handleDraftChange = useCallback((key, patch) => {
    setDrafts(prev => prev.map(draft => {
      if (draft.key !== key) return draft;
      // Editing a field clears its complaint, and any stale failure from a previous save.
      const errors = { ...draft.errors };
      Object.keys(patch).forEach(field => { delete errors[field]; });
      return { ...draft, ...patch, errors, serverError: null };
    }));
  }, []);

  // Desktop edit: the user drops into the same form as another section.
  const handleEditUserInline = (user) => {
    const docId = docIdOf(user);
    setDrafts(prev => {
      if (prev.some(d => d.mode === 'edit' && d.docId === docId)) return prev;
      return [...prev, {
        key: nextDraftKey(),
        mode: 'edit',
        docId,
        email: user.email || '',
        displayName: user.displayName || '',
        company: user.company || '',
        roles: user.roles || [],
        errors: {},
        serverError: null,
      }];
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clone: the same user set up again as a brand new one. Everything is copied,
  // email included — an empty field is more surprising than a filled one you have
  // to change. Saving without changing it is caught by the duplicate check in
  // validateDraft rather than by a failed round trip.
  const handleCloneUser = useCallback((user) => {
    setDrafts(prev => [...prev, {
      ...emptyDraft(),
      email: user.email || '',
      displayName: user.displayName || '',
      company: user.company || '',
      roles: [...(user.roles || [])],
    }]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSaveDrafts = async () => {
    const takenEmails = new Set(
      [...users, ...unassignedUsers]
        .map(u => (u.email || '').trim().toLowerCase())
        .filter(Boolean)
    );

    const validated = drafts.map(draft => {
      const errors = validateDraft(draft, takenEmails);
      // A valid new address is now spoken for, so two cards cannot claim the same one.
      if (draft.mode === 'create' && !errors.email) {
        takenEmails.add(draft.email.trim().toLowerCase());
      }
      return { ...draft, errors };
    });
    if (validated.some(d => Object.keys(d.errors).length > 0)) {
      setDrafts(validated);
      setError('Some sections are incomplete — fix the highlighted fields and save again.');
      return;
    }

    setSavingDrafts(true);
    setError(null);
    setSuccess(null);

    // One call per section, sequentially: same endpoints as the old dialogs.
    const failed = [];
    let created = 0;
    let updated = 0;

    for (const draft of validated) {
      try {
        if (draft.mode === 'create') {
          await api.createUser({
            email: draft.email.trim().toLowerCase(),
            displayName: draft.displayName.trim() || null,
            company: draft.company,
            roles: draft.roles,
          });
          created += 1;
        } else {
          await api.updateUser(draft.docId, {
            displayName: draft.displayName,
            company: draft.company || null,
            roles: draft.roles,
          });
          updated += 1;
        }
        recordRoleSelections(draft.roles);
      } catch (err) {
        console.error('Error saving user draft:', err);
        failed.push({ ...draft, serverError: err.message, errors: {} });
      }
    }

    // Saved sections disappear; failures stay put with their reason so they can be
    // retried. Clearing the last one closes the form back to the Add User button.
    setDrafts(failed);
    setSavingDrafts(false);

    const saved = [
      created > 0 && `${created} user${created === 1 ? '' : 's'} created`,
      updated > 0 && `${updated} user${updated === 1 ? '' : 's'} updated`,
    ].filter(Boolean).join(', ');

    if (saved) {
      setSuccess(saved.charAt(0).toUpperCase() + saved.slice(1));
      setTimeout(() => setSuccess(null), 4000);
    }
    if (failed.length > 0) {
      setError(`${failed.length} section${failed.length === 1 ? '' : 's'} could not be saved — the reason is shown on each one.`);
    }

    loadData();
  };

  const handleSendWelcomeEmail = async (user) => {
    try {
      setError(null);
      setSuccess(null);
      await api.sendWelcomeEmail(docIdOf(user));
      setSuccess(`Welcome email sent to ${user.email}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
      console.error('Error sending welcome email:', err);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      displayName: user.displayName || '',
      email: user.email || '',
      company: user.company || '',
      roles: user.roles || [],
      status: user.status || 'active'
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    setEditUserData({ displayName: '', email: '', company: '', roles: [], status: 'active' });
  };

  const handleUpdateUser = async () => {
    try {
      setError(null);
      await api.updateUser(docIdOf(selectedUser), editUserData);
      handleCloseEditDialog();
      loadData();
    } catch (err) {
      setError(err.message);
      console.error('Error updating user:', err);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      setError(null);
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await api.updateUserStatus(docIdOf(user), newStatus);
      loadData();
    } catch (err) {
      setError(err.message);
      console.error('Error updating user status:', err);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteDialog({ open: true, userId: docIdOf(user), userEmail: user.email });
  };

  const handleDeleteConfirm = async () => {
    try {
      setError(null);
      await api.deleteUser(deleteDialog.userId);
      setUsers(prev => prev.filter(u => docIdOf(u) !== deleteDialog.userId));
      setDeleteDialog({ open: false, userId: null, userEmail: '' });
      setTimeout(() => loadData(), 500);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting user:', err);
      setDeleteDialog({ open: false, userId: null, userEmail: '' });
      loadData();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, userId: null, userEmail: '' });
  };

  const filteredUsers = users.filter(user => {
    // A user doc without a status is treated as active, the same assumption the
    // table's status chip makes.
    if (statusFilter !== 'all' && (user.status || 'active') !== statusFilter) return false;

    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.displayName?.toLowerCase().includes(query) ||
      user.company_name?.toLowerCase().includes(query) ||
      user.roles?.some(role => role.toLowerCase().includes(query))
    );
  });

  const filteredUnassignedUsers = unassignedUsers.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.displayName?.toLowerCase().includes(query) ||
      user.reason?.toLowerCase().includes(query)
    );
  });

  const groupedUsers = groupUsers(filteredUsers, groupBy)
    .map(group => ({ ...group, users: [...group.users].sort(byEmail) }));

  const sharedProps = {
    companies,
    roles,
    filteredUsers,
    filteredUnassignedUsers,
    groupedUsers,
    groupBy,
    setGroupBy,
    GROUP_BY_OPTIONS,
    loading,
    error,
    setError,
    success,
    tabValue,
    setTabValue,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    STATUS_OPTIONS,
    openCreateDialog,
    createData,
    setCreateData,
    formErrors,
    setFormErrors,
    handleOpenCreateDialog,
    handleCloseCreateDialog,
    handleCreateUser,
    openEditDialog,
    selectedUser,
    editUserData,
    setEditUserData,
    handleEditUser,
    handleCloseEditDialog,
    handleUpdateUser,
    drafts,
    savingDrafts,
    handleAddDraft,
    handleRemoveDraft,
    handleDraftChange,
    handleEditUserInline,
    handleCloneUser,
    handleSaveDrafts,
    deleteDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSendWelcomeEmail,
    handleToggleUserStatus,
  };

  return isMobile
    ? <UserManagerMobile {...sharedProps} />
    : <UserManagerDesktop {...sharedProps} />;
}

export default UserManager;
